import type { ChipProps } from "@mui/material/Chip";

export const Filters = {
    pki: 'pki',
    userRole: 'userRole',
    status: 'status',
    updatedDateTime: 'updatedDateTime',
    applicationId: 'applicationId',
    resource: 'resource'
};

export const enum ECommerceShoppingPlatform {
    WooCommerce = 'WooCommerce',
};

export const enum OnboardingApplication {
    ECommerce = 'e',
    HighRisk = 'o'
};

export const enum OnboardingApplicationStatus {
    Approved = 'approved',
    AwaitingReview = 'awaiting_review',
    Blocked = 'blocked',
    Idle = 'idle',
    NotStarted = 'not_started',
    InReview = 'in_review',
    RequestedChanges = 'requested_changes',
    // Deprecated
    Pending = 'pending',
    Rejected = 'rejected',
    // Deprecated
    Incomplete = 'incomplete',
    InProgress = 'in_progress'
};

export const onboardingApplicationStatus = new Map<
    OnboardingApplicationStatus,
    Pick<ChipProps, 'label' | 'color'> & {
        readonly icon?: string
    }
>([
    [OnboardingApplicationStatus.Approved, {
        label: 'Approved',
        color: 'success' as const,
        icon: 'HowToReg'
    }],
    [OnboardingApplicationStatus.AwaitingReview, {
        label: 'Awaiting Review',
        color: 'secondary' as const,
        icon: 'HourglassEmpty'
    }],
    [OnboardingApplicationStatus.Blocked, {
        label: 'Blocked',
        color: 'error' as const,
        icon: 'Block'
    }],
    [OnboardingApplicationStatus.Idle, {
        label: 'Idle',
        color: 'default' as const,
    }],
    [OnboardingApplicationStatus.NotStarted, {
        label: 'Not Started',
        color: 'default' as const,
        icon: 'NotStarted'
    }],
    [OnboardingApplicationStatus.InReview, {
        label: 'In Review',
        color: 'primary' as const,
        icon: 'Visibility'
    }],
    [OnboardingApplicationStatus.RequestedChanges, {
        label: 'Requested Changes',
        color: 'warning' as const,
        icon: 'PublishedWithChanges'
    }],
    [OnboardingApplicationStatus.Pending, {
        label: 'Pending',
        color: 'info' as const,
        icon: 'PendingActions'
    }],
    [OnboardingApplicationStatus.Rejected, {
        label: 'Rejected',
        color: 'error' as const,
        icon: 'ThumbDown'
    }],
    [OnboardingApplicationStatus.Incomplete, {
        label: 'Incomplete',
        color: 'secondary' as const,
        icon: 'Rule'
    }],
    [OnboardingApplicationStatus.InProgress, {
        label: 'In Progress',
        color: 'warning' as const,
        icon: 'Loop'
    }]
]);

export const enum OnboardingApplicationStepAndFieldStatus {
    Approved = 'approved',
    AwaitingReview = 'awaiting_review',
    Rejected = 'rejected',
    RequestedChanges = 'requested_changes'
};

export const onboardingApplicationStepAndFieldStatus = new Map<
    OnboardingApplicationStepAndFieldStatus,
    Pick<ChipProps, 'label' | 'color'>
>([
    [OnboardingApplicationStepAndFieldStatus.Approved, {
        color: 'success' as const,
        label: 'Approved'
    }],
    [OnboardingApplicationStepAndFieldStatus.AwaitingReview, {
        color: 'warning' as const,
        label: 'Awaiting Review'
    }],
    [OnboardingApplicationStepAndFieldStatus.Rejected, {
        color: 'error' as const,
        label: 'Rejected'
    }],
    [OnboardingApplicationStepAndFieldStatus.RequestedChanges, {
        color: 'secondary' as const,
        label: 'Requested Changes'
    }]
]);

export const OnboardingApplicationStep = {
    [OnboardingApplication.ECommerce]: {
        BusinessInformation: 1,
        BankingAndFinance: 2,
        MediaAndComplianceCheck: 3,
        UBOIdentityVerification: 4,
        PaymentMethods: 5
    },
    [OnboardingApplication.HighRisk]: {
        // TODO: Add steps for high-risk merchants
    }
};

export const enum SupportedPaymentMethodsTag {
    Bancontact = 'bancontact',
    Blik = 'blik',
    Eps = 'eps',
    Giropay = 'giropay',
    Ideal = 'ideal',
    Mbway = 'mbway',
    Klarna = 'klarna',
    P24 = 'p24'
};

export const supportedPaymentMethods = {
    [SupportedPaymentMethodsTag.Bancontact]: {
        displayName: 'Bancontact',
        imageUrl: '/assets/images/payment-methods/bancontact.png',
        tag: SupportedPaymentMethodsTag.Bancontact,
    },
    [SupportedPaymentMethodsTag.Blik]: {
        displayName: 'Blik',
        imageUrl: '/assets/images/payment-methods/blik.png',
        tag: SupportedPaymentMethodsTag.Blik,
    },
    [SupportedPaymentMethodsTag.Eps]: {
        displayName: 'EPS',
        imageUrl: '/assets/images/payment-methods/eps.png',
        tag: SupportedPaymentMethodsTag.Eps,
    },
    [SupportedPaymentMethodsTag.Giropay]: {
        displayName: 'Giropay',
        imageUrl: '/assets/images/payment-methods/giropay.png',
        tag: SupportedPaymentMethodsTag.Giropay,
    },
    [SupportedPaymentMethodsTag.Ideal]: {
        displayName: 'iDEAL',
        imageUrl: '/assets/images/payment-methods/ideal.png',
        tag: SupportedPaymentMethodsTag.Ideal,
    },
    [SupportedPaymentMethodsTag.Mbway]: {
        displayName: 'MB WAY',
        imageUrl: '/assets/images/payment-methods/mbway.png',
        tag: SupportedPaymentMethodsTag.Mbway,
    },
    [SupportedPaymentMethodsTag.Klarna]: {
        displayName: 'Klarna',
        imageUrl: '/assets/images/payment-methods/klarna.png',
        tag: SupportedPaymentMethodsTag.Klarna,
    },
    [SupportedPaymentMethodsTag.P24]: {
        displayName: 'P24',
        imageUrl: '/assets/images/payment-methods/p24.png',
        tag: SupportedPaymentMethodsTag.P24,
    }
};

export const enum OnboardingFilableResource {
    BillingFinanceSteps = 'billing-finance-steps',
    UboIdentities = 'ubo-identities',
    ClientDocumentsSteps = 'client-documents-steps'
};

export const multi = [
    Filters.status
];

export const PRE_APPLICATION_PATH = 'pre-application';

export const enum JsonableFields {
    RequestedChangeFields = 'requestedChangeFields',
    FieldsStatuses = 'fieldsStatuses',
    FieldsComments = 'fieldsComments'
};

export const enum ECommerceOnboardingApplication {
    BusinessInformation = 'businessInformation',
    BillingFinance = 'billingFinance',
    MediaCompliance = 'mediaCompliance',
    IdentityVerification = 'identityVerification',
    PaymentMethods = 'paymentMethods'
};
